import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {
  Avatar,
  Chip,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';
import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';

import Button from 'components/ui/Button';
import { setIsSidebarOpen } from 'store/customizationSlice';

const useStyles = makeStyles()((theme) => ({
  listIcon: {
    minWidth: '18px',
    marginTop: 'auto',
    marginBottom: 'auto'
  },
  listCustomIconSub: {
    width: '6px',
    height: '6px'
  },
  listCustomIconSubActive: {
    width: '8px',
    height: '8px'
  },
  listItem: {
    marginBottom: '5px',
    alignItems: 'center'
  },
  listItemNoBack: {
    marginBottom: '5px',
    backgroundColor: 'transparent !important',
    paddingTop: '8px',
    paddingBottom: '8px',
    alignItems: 'flex-start'
  },
  subMenuCaption: {
    ...theme.typography.subMenuCaption
  }
}));

const ListItemComponent = forwardRef((props, ref) => <Link {...props} to={props.href} ref={ref} />);

const NavItem = ({ item, level }) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();

  const customization = useSelector((state) => state.customization);
  const matchesSM = useMediaQuery(theme.breakpoints.down('md'));

  const isItemSelected = item.url 
    ? location.pathname === item.url || location.pathname.startsWith(item.url + '/')
    : location.pathname
        .toString()
        .split('/')
        .findIndex((id) => id === item.id) > -1;

  const Icon = item.icon;
  const itemIcon = item.icon ? (
    <Icon stroke={1.5} size="1.3rem" className={classes.listCustomIcon} />
  ) : (
    <FiberManualRecordIcon
      className={isItemSelected ? classes.listCustomIconSubActive : classes.listCustomIconSub}
      fontSize={level > 0 ? 'inherit' : 'default'}
    />
  );

  let itemIconClass = !item.icon ? classes.listIcon : classes.menuIcon;

  itemIconClass = customization.navType === 'nav-dark'
    ? [itemIconClass, classes.listCustomIcon].join(' ')
    : itemIconClass;

  const handleItemClick = () => {
    if (matchesSM) dispatch(setIsSidebarOpen(false));
  };

  return (
    <ListItemButton
      component={ListItemComponent}
      href={item.url}
      disabled={item.disabled}
      className={level > 1 ? classes.listItemNoBack : classes.listItem}
      sx={{ borderRadius: `${customization.borderRadius}px` }}
      selected={isItemSelected}
      onClick={() => handleItemClick(item.id)}
      target={item.isNewTab ? '_blank' : '_self'}
      style={{ paddingLeft: `${level * 23}px` }}>
      {!item.button && (
        <>
          <ListItemIcon className={itemIconClass}>{itemIcon}</ListItemIcon>
          <ListItemText
            primary={(
              <Typography variant={isItemSelected ? 'h5' : 'body1'} color="inherit">
                {item.title}
              </Typography>
            )}
            secondary={
              item.caption && (
                <Typography
                  variant="caption"
                  className={classes.subMenuCaption}
                  display="block"
                  gutterBottom>
                  {item.caption}
                </Typography>
              )
            }
          />
        </>
      )}
      {item.chip && (
        <Chip
          color={item.chip.color}
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
        />
      )}
      {item.button && (
        <Button variant={item.button.variant} color={item.button.color} sx={{ width: '100%' }}>
          {item.title}
        </Button>
      )}
    </ListItemButton>
  );
};

NavItem.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number
};

export default NavItem;
